/**
 * MetaFile — TOML `.meta` sidecar files (git source of truth for asset identity).
 *
 * Every source asset under /content gets a `<file>.meta` sidecar holding its GUID,
 * importer type, importer params, and labels. The sidecar is what makes a project
 * survive `git clone`: FineIndex adopts GUIDs from sidecars instead of minting new
 * ones, so GUID references in committed content (materials → textures, scenes →
 * assets) stay intact on any machine. The SQLite index is a rebuildable cache OF
 * the sidecars + import state, never the source of truth.
 *
 * Format is TOML (Godot-`.import`-style: comments allowed, low noise); emitted
 * canonically (fixed top-level key order, recursively sorted params, LF, trailing
 * newline) so a no-op rewrite is byte-identical and diffs stay minimal. Sidecars
 * hold identity + user intent ONLY — no timestamps/hashes/import status, those are
 * cache state and belong to the DB.
 *
 * @module @engine/aid/MetaFile
 */

import { parse as parseToml } from 'smol-toml';
import { Logger } from '@engine/logger';
import type { AssetEntry } from './types';

const log = Logger.create('MetaFile');

/** Bump when the sidecar schema changes shape (readers warn on newer versions). */
export const META_VERSION = 1;

export const META_EXT = '.meta';

/**
 * Git-versioned identity of an asset. Mirrors the identity subset of AssetEntry.
 */
export interface AssetMeta {
  metaVersion: number;
  guid: string;
  importerType: string;
  importerParams: Record<string, unknown>;
  labels: string[];
}

// ============================================
// PATH HELPERS
// ============================================

/** True for any sidecar path. NOTE: cache artifacts like `/.cache/<guid>.png.meta`
 * also match — callers must exclude dot-dirs first (FineIndex.shouldSkipFile does). */
export function isMetaPath(path: string): boolean {
  return path.endsWith(META_EXT);
}

/** Sidecar path for an asset (`/content/hero.png` → `/content/hero.png.meta`). */
export function metaPathFor(assetPath: string): string {
  if (isMetaPath(assetPath)) {
    throw new Error(`Asset path is already a meta path: ${assetPath}`);
  }
  return assetPath + META_EXT;
}

/** Asset path for a sidecar (`/content/hero.png.meta` → `/content/hero.png`). */
export function assetPathForMeta(metaPath: string): string {
  if (!isMetaPath(metaPath)) {
    throw new Error(`Not a meta path: ${metaPath}`);
  }
  return metaPath.slice(0, -META_EXT.length);
}

// ============================================
// CANONICAL TOML EMIT
// ============================================

/**
 * GUIDs are normally crypto.randomUUID(), but hand-authored sidecars are legal —
 * accept any printable-ASCII token so adoption never silently re-mints over a
 * merely unconventional (still unique) id.
 */
const GUID_PATTERN = /^[\x21-\x7e]{1,64}$/;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** TOML basic strings share JSON's escape rules for everything we emit. */
function tomlString(value: string): string {
  return JSON.stringify(value);
}

const BARE_KEY = /^[A-Za-z0-9_-]+$/;

function tomlKey(key: string): string {
  return BARE_KEY.test(key) ? key : tomlString(key);
}

/**
 * Inline TOML value. Objects become sorted inline tables, arrays stay in source
 * order (order is semantic). `undefined`/`null` are the caller's job to strip —
 * TOML has no null, so hitting one here is a bug in the entry data.
 */
function tomlValue(value: unknown): string {
  switch (typeof value) {
    case 'string':
      return tomlString(value);
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      if (!Number.isFinite(value)) {
        // TOML supports inf/nan literals; keep round-trip fidelity
        return Number.isNaN(value) ? 'nan' : (value > 0 ? 'inf' : '-inf');
      }
      // String(2) → TOML integer, String(0.5) → TOML float; JS Number round-trips identically
      return String(value);
    case 'object': {
      if (value === null) throw new Error('TOML has no null — strip null values before emit');
      if (Array.isArray(value)) {
        return `[${value.map(v => tomlValue(v)).join(', ')}]`;
      }
      const obj = value as Record<string, unknown>;
      const keys = Object.keys(obj).filter(k => obj[k] !== undefined && obj[k] !== null).sort();
      return `{ ${keys.map(k => `${tomlKey(k)} = ${tomlValue(obj[k])}`).join(', ')} }`;
    }
    default:
      throw new Error(`Cannot emit ${typeof value} as TOML`);
  }
}

/**
 * Canonical sidecar bytes: fixed top-level order (metaVersion, guid, importerType,
 * labels), then `[importerParams]` with recursively sorted keys; nested objects as
 * sorted inline tables. LF, trailing newline, `labels` omitted when empty.
 * Emitting the same meta twice is byte-identical (unit-tested).
 */
export function emitCanonicalToml(meta: AssetMeta): string {
  const lines: string[] = [];
  lines.push(`metaVersion = ${meta.metaVersion}`);
  lines.push(`guid = ${tomlString(meta.guid)}`);
  lines.push(`importerType = ${tomlString(meta.importerType)}`);
  if (meta.labels.length > 0) {
    lines.push(`labels = ${tomlValue(meta.labels)}`);
  }

  const paramKeys = Object.keys(meta.importerParams)
    .filter(k => meta.importerParams[k] !== undefined && meta.importerParams[k] !== null)
    .sort();
  if (paramKeys.length > 0) {
    lines.push('');
    lines.push('[importerParams]');
    for (const key of paramKeys) {
      lines.push(`${tomlKey(key)} = ${tomlValue(meta.importerParams[key])}`);
    }
  }

  return lines.join('\n') + '\n';
}

/** Identity subset of an AssetEntry, ready for emit. */
export function metaFromEntry(entry: Pick<AssetEntry, 'guid' | 'importerType' | 'importerParams' | 'labels'>): AssetMeta {
  return {
    metaVersion: META_VERSION,
    guid: entry.guid,
    importerType: entry.importerType,
    importerParams: entry.importerParams ?? {},
    labels: entry.labels ?? [],
  };
}

// ============================================
// PARSE + VALIDATE
// ============================================

/**
 * smol-toml returns TomlDate instances for datetime values; our schema has none,
 * so normalize any non-plain leaf to a JSON-safe primitive defensively.
 */
function toPlainValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(toPlainValue);
  if (typeof value === 'object' && value !== null) {
    if (value instanceof Date) return value.toISOString();
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = toPlainValue(v);
    return out;
  }
  return value;
}

/**
 * Parse sidecar text. Returns null (never throws) on anything malformed — a
 * corrupt meta must degrade to "no meta" (mint + overwrite), not crash refresh.
 */
export function parseMeta(text: string, sourceLabel = '<meta>'): AssetMeta | null {
  let raw: Record<string, unknown>;
  try {
    raw = parseToml(text) as Record<string, unknown>;
  } catch (error) {
    log.warn(`Corrupt meta ${sourceLabel} (unparseable TOML) — treating as absent:`, error);
    return null;
  }

  const guid = raw['guid'];
  if (typeof guid !== 'string' || !GUID_PATTERN.test(guid)) {
    log.warn(`Corrupt meta ${sourceLabel} (bad guid) — treating as absent`);
    return null;
  }

  const importerType = raw['importerType'];
  if (typeof importerType !== 'string' || importerType.length === 0) {
    log.warn(`Corrupt meta ${sourceLabel} (bad importerType) — treating as absent`);
    return null;
  }

  const metaVersion = typeof raw['metaVersion'] === 'number' ? raw['metaVersion'] : META_VERSION;
  if (metaVersion > META_VERSION) {
    // Newer engine wrote this — identity fields are still forward-safe to adopt
    log.warn(`Meta ${sourceLabel} has newer metaVersion ${metaVersion} (this engine: ${META_VERSION})`);
  }

  const rawParams = raw['importerParams'];
  const importerParams = isPlainObject(rawParams) ? toPlainValue(rawParams) as Record<string, unknown> : {};

  const rawLabels = raw['labels'];
  const labels = Array.isArray(rawLabels) ? rawLabels.filter((l): l is string => typeof l === 'string') : [];

  return { metaVersion, guid, importerType, importerParams, labels };
}

// ============================================
// VFS IO
// ============================================

/** Structural subset of VirtualFileSystem used here (keeps unit tests hermetic). */
export interface MetaFileVfs {
  readFile(path: string): Promise<{ content: string | ArrayBuffer } | null>;
  writeFile(path: string, content: string | ArrayBuffer, type?: string): Promise<void>;
  deleteFile(path: string): Promise<boolean>;
}

function contentToString(content: string | ArrayBuffer): string {
  return typeof content === 'string' ? content : new TextDecoder().decode(content);
}

/** Read + parse the sidecar for an asset. Null when absent or corrupt. */
export async function readMeta(vfs: MetaFileVfs, assetPath: string): Promise<AssetMeta | null> {
  const metaPath = metaPathFor(assetPath);
  let file: { content: string | ArrayBuffer } | null;
  try {
    file = await vfs.readFile(metaPath);
  } catch {
    return null;
  }
  if (!file) return null;
  return parseMeta(contentToString(file.content), metaPath);
}

/**
 * Write the canonical sidecar for an asset. Skips the write when the existing
 * sidecar is SEMANTICALLY identical (parsed content matches), not just
 * byte-identical — so hand-formatting and comments in a user's sidecar survive
 * adoption, and our own write echoes re-enter the watcher as no-ops.
 *
 * @returns true if a write actually happened
 */
export async function writeMeta(vfs: MetaFileVfs, assetPath: string, meta: AssetMeta): Promise<boolean> {
  const metaPath = metaPathFor(assetPath);
  const next = emitCanonicalToml(meta);
  try {
    const existing = await vfs.readFile(metaPath);
    if (existing) {
      const parsed = parseMeta(contentToString(existing.content), metaPath);
      if (parsed && emitCanonicalToml(parsed) === next) {
        return false;
      }
    }
  } catch {
    // unreadable existing file — fall through and overwrite
  }
  await vfs.writeFile(metaPath, next, 'text');
  return true;
}

/** Delete the sidecar for an asset (no-op if absent). */
export async function deleteMeta(vfs: MetaFileVfs, assetPath: string): Promise<void> {
  try {
    await vfs.deleteFile(metaPathFor(assetPath));
  } catch {
    // sidecar absent or backend refused — nothing to clean up
  }
}

/**
 * Move the sidecar together with its asset. Copies raw bytes (preserves any user
 * comments in the TOML) and deletes the old sidecar. Absent sidecar is a no-op —
 * the caller's persistMeta will materialize one at the new path.
 */
export async function moveMeta(vfs: MetaFileVfs, fromAssetPath: string, toAssetPath: string): Promise<void> {
  const fromMeta = metaPathFor(fromAssetPath);
  const toMeta = metaPathFor(toAssetPath);
  try {
    const file = await vfs.readFile(fromMeta);
    if (!file) return;
    await vfs.writeFile(toMeta, file.content, 'text');
    await vfs.deleteFile(fromMeta);
  } catch (error) {
    log.warn(`Failed to move meta ${fromMeta} → ${toMeta}:`, error);
  }
}
